import React, { useState, useEffect, useRef } from "react";
import { SponsorPageStyle } from "./Configuration.style";
import TopBar from "../../components/TopBar/TopBar";
import Popup from "../../components/Modals/Modal";
import { MainBodyStyle } from "../../styles/MainBodyStyles";
import Footer from "../../components/footer/Footer";
import { toast } from "react-toastify";
import { useDB } from "../../context/DynamoDBContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useReload } from "../../context/ReloadContext";
import { deleteSponsor, getAllSponsors } from "../../api/SponsorshipApi";
import { useNavigate } from "react-router-dom";
import { HeadingStyle } from "../../styles/HeadingStyles";
import ROUTE from "../../common/Routes";
import { convertTime2 } from "../../common/ConvertTime";
import { useSelector } from "react-redux";
import { getSponsorAnalytics } from "../../api/SponsorshipApi";

const SponsorAccountList = () => {
  const { dynamoDBClient } = useDB();
  const navigate = useNavigate();
  const { access_token } = useSelector((state) => state.userKeyRed);
  const queryClient = useQueryClient();
  const { reload } = useReload();
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState();
  const [enableApi, setEnableApi] = useState(false);
  const [data, setData] = useState([]);
  const [disableIcons, setDisableIcons] = useState(false);
  const [disableViewIcon, setDisableViewIcon] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  
  // New states for dropdown visibility and selected sponsor
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  useEffect(() => {
    if (dynamoDBClient) {
      setEnableApi(true);
    } else {
      setEnableApi(false);
    }
  }, [dynamoDBClient]);

  useEffect(() => {
    if (reload) {
      queryClient.fetchQuery(["getAllSponsors"]);
    }
  }, [reload]);

  const handleError = (type) => {
    if (type === "getAllSponsors") {
      toast.error("Error while loading Sponsors", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
  };

  const transformSponsorData = (apiData) => {
    console.log("API Data Received:", apiData);

    return apiData.map((item) => ({
      sponsorId: item.sponsorId,
      name: item.sponsorName,
      id: item.sponsorId,
      logoImage: item.logoImage,
      sponsorshipPlacement: (item.sponsorPlacementList || []).map((placement) => ({
        sponsorPlacementId: placement.sponsorPlacementId,
        type: placement.sponsorPlacementType,
        startDate: placement.startDate,
        endDate: placement.endDate,
        duration: placement.duration,
        slot: placement.slot,
      })),
    }));
  };

  const { data: configData, status: configStatus } = useQuery({
    queryKey: ["getAllSponsors"],
    queryFn: () => getAllSponsors(access_token),
    enabled: enableApi,
    onSuccess: (response) => {
      if (response.data.length > 0) {
        const transformed = transformSponsorData(response.data);
        console.log("Transformed Data:", transformed);
        setData(transformed);
      } else {
        setData([]);
      }
      setEnableApi(false);
    },
    onError: (error) => {
      console.log("getAllSponsors :", error);
      handleError("getAllSponsors");
    },
  });

  const removeSponsor = async (sponsorId, access_token) => {
    try {
      const val = data.filter((item) => item.sponsorId != sponsorId);
      const response = await deleteSponsor(sponsorId, access_token);
      toast("Successfully Removed Sponsor");
      setData(val);
      setDeleteId(sponsorId);
    } catch (error) {
      toast.error("Error has Ocurred.Try Again", {
        bodyClassName: "notification-div-error",
        progressClassName: "progress-bar-error",
      });
    }
  };

  // Function to handle the click of the eye button and send placement IDs
  const handleViewAnalytics = (sponsorId) => {
    const sponsor = data.find(item => item.sponsorId === sponsorId);

    if (sponsor && sponsor.sponsorshipPlacement) {
      // Extract placement IDs for the selected sponsor
      const placementIds = sponsor.sponsorshipPlacement.map(placement => placement.sponsorPlacementId);
      console.log("Selected Sponsor Placement IDs:", placementIds);

      // Fetch analytics data using the placement IDs
      sendPlacementIds(placementIds);
    }
  };

  const sendPlacementIds = async (placementIds) => {
    console.log("Sending Placement IDs to API:", placementIds);

    try {
      // Fetch actual analytics data from the API
      const analyticsData = await getSponsorAnalytics(placementIds, access_token);

      // Navigate to the Analytics screen with the fetched data
      navigate(`/analytics/${placementIds.join(",")}`, {
        state: { analyticsData } // Pass the analytics data to the next screen
      });

      toast.success("Analytics data fetched successfully");
    } catch (error) {
      console.error("Error in sending placement IDs:", error);
      toast.error("Failed to fetch analytics data");
    }
  };

  // Toggle Dropdown Visibility
  const toggleCustomerDropdown = () => {
    setShowCustomerDropdown(v => !v);
  };
  
  // Ref for dropdown wrapper
  const dropdownRef = useRef(null);
  
  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
    }
    if (showCustomerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomerDropdown]);

  // Set the selected sponsor
  const handleSponsorSelection = (sponsorName) => {
    setSelectedSponsor(sponsorName);
    setShowDropdown(false); // Hide dropdown after selection
  };

  const handleCustomerReportClick = (sponsor) => {
    setShowCustomerDropdown(false);
    navigate(ROUTE.DownloadSponsorReport, {
      state: { sponsorName: sponsor.name, sponsorId: sponsor.sponsorId },
    });
  };
  return (
    <SponsorPageStyle>
      <TopBar authorized={true} />
      <Popup
        title="Delete Sponsor"
        visible={showModal}
        setVisible={() => setShowModal(false)}
        message="This Action will DELETE the Sponsor and its configurations. Are you sure you want to proceed?"
        onAccept={() => {
          setShowModal(false);
          removeSponsor(deleteId, access_token);
        }}
      />
      <MainBodyStyle>
        {/* ---- NEW HEADER WITH SMALL BUTTON ---- */}
      {/* ----- Header with dropdown ----- */}
      <div ref={dropdownRef} style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",       // for absolute menu
      }}>
        <HeadingStyle style={{ marginLeft: "650px" }}>
          Sponsors Accounts List
        </HeadingStyle>

        {/* button + menu wrapper */}
        <div style={{ position: "relative", marginLeft: "20px", marginBottom: "35px" }}>
          <button
            className="btn form-button dropdown-toggle"
            type="button"
            onClick={toggleCustomerDropdown}
            aria-expanded={showCustomerDropdown}
            style={{ padding: "16px 20px", fontSize: "0.9rem", height: "auto", lineHeight: 1.2 }}
          >
            Customer Reports <i className="bi bi-caret-down-fill ms-1" />
          </button>

          {showCustomerDropdown && (
            <ul className="custom-dropdown-menu" style={{ width: "100%" }}>
              {data.map((sponsor) => (
                <li className="custom-dropdown-item"
                  key={sponsor.sponsorId}
                  onClick={() => handleCustomerReportClick(sponsor)}
                  onMouseDown={e => e.preventDefault()}
                 >
                   {sponsor.name}
                 </li>
              ))}
            </ul>
          )}
        </div>
      </div>

        <div>
          <div className="table-container">
            <div className="table-div2">
              <table className="table table-hover align-middle">
                <thead className="table-head">
                  <tr>
                    <th scope="col" className="table-item">#</th>
                    <th scope="col" className="table-item">Sponsors Name</th>
                    <th scope="col" className="table-item">Sponsors Logo</th>
                    <th scope="col" className="table-item">Sponsorship Placements</th>
                    <th scope="col" className="table-item">Sponsorship Duration (Days)</th>
                    <th scope="col" className="table-item">Start Date</th>
                    <th scope="col" className="table-item">End Date</th>
                    <th scope="col" className="table-item">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {configStatus === "loading" || reload ? (
                    <tr><td colSpan={8}><p className="table-loading">Loading...</p></td></tr>
                  ) : data.length === 0 ? (
                    <tr><td colSpan={8}><p className="table-loading">No Sponsors Added</p></td></tr>
                  ) : (
                    data.map((item, index) => (
                      <tr key={item.sponsorId}>
                        <th scope="row" className="table-item">{index + 1}</th>
                        <td className="table-item table-item2">{item.name}</td>
                        <td className="table-item table-item2">
                          <img src={item.logoImage} width={120} height={100} />
                        </td>
                        <td className="table-item table-item2">
                          {item.sponsorshipPlacement.map((placement, idx) => (
                            <p key={idx}>{placement.type}</p>
                          ))}
                        </td>
                        <td className="table-item table-item2">
                          {item.sponsorshipPlacement.map((placement, idx) => (
                            <p key={idx}>{placement.duration}</p>
                          ))}
                        </td>
                        <td className="table-item table-item2">
                          {item.sponsorshipPlacement.map((placement, idx) => (
                            <p key={idx}>{convertTime2(placement.startDate)}</p>
                          ))}
                        </td>
                        <td className="table-item table-item2">
                          {item.sponsorshipPlacement.map((placement, idx) => (
                            <p key={idx}>{convertTime2(placement.endDate)}</p>
                          ))}
                        </td>
                        <td>
                          <div>
                            <i
                              className={disableViewIcon ? "bi bi-eye view-icon disable-view-icon" : "bi bi-eye view-icon"}
                              onClick={() => handleViewAnalytics(item.sponsorId)}
                            ></i>
                            <i
                              title="Edit"
                              className={disableIcons ? "bi bi-pencil edit-icon disable-icon" : "bi bi-pencil edit-icon"}
                              onClick={() => navigate(ROUTE.UpdateSponsor, { state: { item: item } })}
                            />
                            <i
                              title="Delete"
                              className={disableIcons ? "bi bi-trash delete-icon disable-icon" : "bi bi-trash delete-icon"}
                              onClick={() => {
                                setDeleteId(item.sponsorId);
                                setShowModal(true);
                              }}
                            />
                          </div>
                          {/* <div className="btn-div">
                            <button
                              className="btn form-button"
                              onClick={() => navigate(ROUTE.DownloadSponsorReport, { state: { sponsorName: item.name, sponsorId: item.sponsorId } })}
                            >
                              Download Report
                            </button>
                          </div> */}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </MainBodyStyle>
      <Footer style="footer" />
    </SponsorPageStyle>
  );
};

export default SponsorAccountList;
